import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api/api";
import { ERROR_MESSAGES } from "../../constants/enums";
import {
  ILogEntry,
  IUserTimelineEntry,
  IModuleUsageEntry,
  ITopUserEntry,
  ISlowApiEntry,
  IErrorRateEntry,
  IBankingAccessLogEntry,
} from "./accessLogs.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

export const logAccess = createAsyncThunk<void, ILogEntry>(
  "accessLogs/logAccess",
  async (payload) => {
    if (
      !payload.module_name?.trim() ||
      !payload.sub_module_name?.trim() ||
      !payload.action_name?.trim()
    ) {
      console.warn("logAccess: missing required fields");
      return;
    }
    try {
      await api.post("/api/log-access", payload);
    } catch {
      // Silently fail
    }
  },
);

export const fetchUserTimeline = createAsyncThunk<
  { data: IUserTimelineEntry[]; totalRecords: number },
  {
    pageNumber: number;
    rowsInPerPage: number;
    fromDate: string;
    toDate: string;
  },
  { rejectValue: { message: string; status?: number } }
>(
  "accessLogs/fetchUserTimeline",
  async ({ pageNumber, rowsInPerPage, fromDate, toDate }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/log-access/get-user-timeline?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${fromDate}&to_date=${toDate}`,
      );
      return {
        data: res.data.data,
        totalRecords: res.data.totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);


export const fetchModuleUsage = createAsyncThunk<
  { data: IModuleUsageEntry[]; totalRecords: number },
  {
    pageNumber: number;
    rowsInPerPage: number;
    fromDate: string;
    toDate: string;
  },
  { rejectValue: { message: string; status?: number } }
>(
  "accessLogs/fetchModuleUsage",
  async ({ pageNumber, rowsInPerPage, fromDate, toDate }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/log-access/get-module-usage?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${fromDate}&to_date=${toDate}`,
      );
      return {
        data: res.data.data,
        totalRecords: res.data.totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchTopUsers = createAsyncThunk<
  { data: ITopUserEntry[]; totalRecords: number },
  { fromDate: string; toDate: string },
  { rejectValue: { message: string; status?: number } }
>(
  "accessLogs/fetchTopUsers",
  async ({ fromDate, toDate }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/log-access/get-top-users?from_date=${fromDate}&to_date=${toDate}`,
      );
      return {
        data: res.data.data,
        totalRecords: res.data.totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchSlowApis = createAsyncThunk<
  { data: ISlowApiEntry[]; totalRecords: number },
  { fromDate: string; toDate: string },
  { rejectValue: { message: string; status?: number } }
>(
  "accessLogs/fetchSlowApis",
  async ({ fromDate, toDate }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/log-access/get-slow-apis?from_date=${fromDate}&to_date=${toDate}`,
      );
      return {
        data: res.data.data,
        totalRecords: res.data.totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchErrorRate = createAsyncThunk<
  { data: IErrorRateEntry[]; totalRecords: number },
  { fromDate: string; toDate: string },
  { rejectValue: { message: string; status?: number } }
>(
  "accessLogs/fetchErrorRate",
  async ({ fromDate, toDate }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/log-access/get-error-rate?from_date=${fromDate}&to_date=${toDate}`,
      );
      return {
        data: res.data.data,
        totalRecords: res.data.totalRecords,
      };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);

export const fetchBankingLogs = createAsyncThunk<
  { data: IBankingAccessLogEntry[]; totalRecords: number },
  {
    pageNumber: number;
    rowsInPerPage: number;
    fromDate: string;
    toDate: string;
    searchState?: IDatatableV2AdvancedSearchFilter;
  },
  { rejectValue: { message: string; status?: number } }
>(
  "accessLogs/fetchBankingLogs",
  async ({ pageNumber, rowsInPerPage, fromDate, toDate, searchState }, { rejectWithValue }) => {
    try {
      let url = `/banking-access-log?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}&from_date=${fromDate}&to_date=${toDate}`;
      if (searchState && searchState.length > 0) {
        url += `&advanceFilter=${JSON.stringify(searchState)}`;
      }
      const res = await api.get(url);
      return { data: res.data.data, totalRecords: res.data.totalRecords };
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR,
        status: err.response?.status,
      });
    }
  },
);